/**
 * Object identity stabilizers.
 *
 * Some consumers key their behaviour on object identity rather than content.
 * The costly one is the SDK's `MeetingSourceElement`: it rebuilds the media of
 * a source whenever the `source` prop changes identity, which drops the tracks
 * currently being emitted. Since the resolved settings are handed out as a
 * fresh deep copy on every update (see `helpers/settings.ts`), a source object
 * that never changed can still arrive "new" — hence these helpers, used to
 * hand back the previous object when the content is unchanged.
 */

import deepEqual from "deep-equal";

/** `prev` when it is structurally equal to `next`, otherwise `next`. */
export const keepIdentity = <T>(prev: T | undefined, next: T): T =>
  prev !== undefined && deepEqual(prev, next) ? prev : next;

/**
 * Element-wise {@link keepIdentity}. The array itself is also reused when every
 * element was kept, so `prev === result` means "nothing changed at all".
 */
export const keepArrayIdentity = <T>(prev: T[] | undefined, next: T[]): T[] => {
  if (!prev || prev.length !== next.length) return next;
  const merged = next.map((item, index) => keepIdentity(prev[index], item));
  return merged.every((item, index) => item === prev[index]) ? prev : merged;
};
